/**
 * The quote card (requirement 0138, ADR-0209 §4): a saved quote drawn as a PNG
 * on a canvas, from the spec in testdata/quote-card/card.json that the app's
 * card follows too. quotePicture.test.ts holds this copy to it; a change
 * starts there.
 *
 * Drawn with the canvas's own text, not a picture of the DOM: the browser
 * shapes Persian and runs the bidi algorithm in fillText as it does in a
 * page, and the self-hosted faces (ADR-0076) are the page's own, so nothing
 * has to be serialised and re-embedded.
 */

export const SPEC = {
  width: 1080,
  height: 1350,
  fileName: "sageleaf-quote.png",
  padX: 96,
  padTop: 120,
  padBottom: 64,
  quote: { max: 60, floor: 34, step: 2, lineLtr: 1.45, lineRtl: 1.8 },
  rule: { width: 72, height: 3, before: 44, after: 28 },
  title: { size: 36, line: 1.3, maxLines: 2, weight: 700 },
  byline: { size: 28, line: 1.4, maxLines: 2, gap: 8 },
  footer: {
    height: 64,
    ruleGap: 40,
    rule: 2,
    mark: 52,
    markGap: 16,
    name: "SageLeaf",
    nameSize: 34,
    address: "sageleaf.io",
    addressSize: 28,
  },
  faces: { serif: "Merriweather", sans: "Vazirmatn", fallback: "Vazirmatn" },
  rtlLangs: ["fa", "ar", "he", "ur", "ps", "ckb", "yi", "dv"],
  tint: {
    wash: 0.12,
    washLightness: 0.5,
    accentLightness: 0.3,
    saturation: 0.45,
    grey: 0.08,
    darken: 0.88,
    contrast: 4.5,
    maxSteps: 24,
  },
} as const;

export interface CardLook {
  id: "vintage" | "modern";
  ground: string;
  ink: string;
  muted: string;
  line: string;
  brand: string;
  accent: string;
  quoteFace: string;
  metaFace: string;
  italicLatin: boolean;
  frame?: { inset: number; width: number };
  bar?: { height: number };
}

export const LOOKS: Record<CardLook["id"], CardLook> = {
  vintage: {
    id: "vintage",
    ground: "#f6eedb",
    ink: "#3a2e20",
    muted: "#86765e",
    line: "#d9c9a6",
    brand: "#8a6a2e",
    accent: "#8a6a2e",
    quoteFace: SPEC.faces.serif,
    metaFace: SPEC.faces.serif,
    italicLatin: true,
    frame: { inset: 36, width: 2 },
  },
  modern: {
    id: "modern",
    ground: "#fffefa",
    ink: "#1c2118",
    muted: "#757d6f",
    line: "#dde2d5",
    brand: "#4a7a2a",
    accent: "#4a7a2a",
    quoteFace: SPEC.faces.serif,
    metaFace: SPEC.faces.sans,
    italicLatin: false,
    bar: { height: 14 },
  },
};

export type Dir = "ltr" | "rtl";

/** The card's direction: its book's language's, never the interface's. */
export function cardDirection(lang: string | undefined): Dir {
  const base = (lang ?? "").split("-")[0]!.toLowerCase();
  return (SPEC.rtlLangs as readonly string[]).includes(base) ? "rtl" : "ltr";
}

const RTL = /[\u0590-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF\u{10800}-\u{10FFF}\u{1E800}-\u{1EFFF}]/u;

/** Strong direction of one character: 1 left to right, -1 right to left, 0 neutral. */
function strong(ch: string): 1 | -1 | 0 {
  if (RTL.test(ch)) return -1;
  const r = ch.codePointAt(0)!;
  if ((r >= 0x41 && r <= 0x5a) || (r >= 0x61 && r <= 0x7a)) return 1;
  if (r < 0xc0 || r === 0xd7 || r === 0xf7) return 0;
  if (r >= 0x02b9 && r <= 0x036f) return 0;
  if (r <= 0x058f) return 1;
  if (r >= 0x0900 && r <= 0x1fff) return 1;
  if (r >= 0x2c00 && r <= 0x2dff) return 1;
  if (r >= 0x3040 && r <= 0x9fff) return 1;
  if (r >= 0xac00 && r <= 0xd7af) return 1;
  return 0;
}

/** The first strong character's direction, as dir="auto" decides it. */
export function directionOf(text: string, fallback: Dir): Dir {
  for (const ch of text) {
    const s = strong(ch);
    if (s !== 0) return s < 0 ? "rtl" : "ltr";
  }
  return fallback;
}

/**
 * Wraps every run of the other direction in FSI…PDI, so the neutral quotes
 * and punctuation beside a Latin run in a Persian line keep their ends
 * (CLAUDE.md § Persian and RTL). `paragraph` is the quote's own direction.
 */
export function isolateRuns(text: string, paragraph: Dir): string {
  const chars = Array.from(text);
  const other = paragraph === "rtl" ? 1 : -1;
  let out = "";
  let i = 0;
  while (i < chars.length) {
    if (strong(chars[i]!) !== other) {
      out += chars[i++];
      continue;
    }
    let last = i;
    for (let j = i + 1; j < chars.length; j++) {
      const s = strong(chars[j]!);
      if (s === -other) break;
      if (s === other) last = j;
    }
    out += "\u2068" + chars.slice(i, last + 1).join("") + "\u2069";
    i = last + 1;
  }
  return out;
}

/** What goes beside the picture: the title, the author and the book's link. */
export function shareText(
  title: string,
  author: string | undefined,
  url: string | undefined | null,
) {
  const head = author ? `\u2068${title}\u2069 · \u2068${author}\u2069` : `\u2068${title}\u2069`;
  return url ? `${head}\n${url}` : head;
}

// Colour ---------------------------------------------------------------------

type RGB = [number, number, number];

/** Half up, as the spec rounds everywhere. */
const round = (x: number) => Math.floor(x + 0.5);

export const hex = (c: RGB) => "#" + c.map((v) => v.toString(16).padStart(2, "0")).join("");
export const rgbOf = (s: string): RGB =>
  [1, 3, 5].map((i) => parseInt(s.slice(i, i + 2), 16)) as RGB;

/** The mean sRGB colour of RGBA pixels, alpha ignored. */
export function meanColour(rgba: ArrayLike<number>): RGB {
  let r = 0,
    g = 0,
    b = 0;
  const n = Math.floor(rgba.length / 4);
  for (let i = 0; i < n * 4; i += 4) {
    r += rgba[i]!;
    g += rgba[i + 1]!;
    b += rgba[i + 2]!;
  }
  return [round(r / n), round(g / n), round(b / n)];
}

function luminance(c: RGB): number {
  const lin = (v: number) => {
    const x = v / 255;
    return x <= 0.04045 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
}

export function contrastOf(a: RGB, b: RGB): number {
  const la = luminance(a),
    lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const mod = (a: number, n: number) => ((a % n) + n) % n;

function toHsl(c: RGB): [number, number, number] {
  const [r, g, b] = c.map((v) => v / 255) as RGB;
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  if (mx === mn) return [0, 0, l];
  const d = mx - mn;
  const s = d / (1 - Math.abs(2 * l - 1));
  const h = mx === r ? mod((g - b) / d, 6) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}

function fromHsl(h: number, s: number, l: number): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(mod(h / 60, 2) - 1));
  const m = l - c / 2;
  const sectors: [number, number, number][] = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ];
  const [r, g, b] = sectors[mod(Math.floor(h / 60), 6)]!;
  return [round((r + m) * 255), round((g + m) * 255), round((b + m) * 255)];
}

/**
 * A card's two colours from its cover's mean: the cover's hue, held to a floor
 * of saturation (a painted cover's mean is muddy), washed into the paper and
 * set as a dark accent that reads on it. A grey cover stays grey; no mean, the
 * look's own.
 */
export function tintFor(look: CardLook, mean: RGB | null): { ground: string; accent: string } {
  if (!mean) return { ground: look.ground, accent: look.accent };
  const t = SPEC.tint;
  const [h, s0] = toHsl(mean);
  const s = s0 < t.grey ? s0 : Math.max(s0, t.saturation);
  const wash = fromHsl(h, s, t.washLightness);
  const paper = rgbOf(look.ground);
  const ground = paper.map((v, i) => round(v * (1 - t.wash) + wash[i]! * t.wash)) as RGB;
  let accent = fromHsl(h, s, t.accentLightness);
  for (let n = 0; n < t.maxSteps && contrastOf(accent, ground) < t.contrast; n++) {
    accent = accent.map((v) => round(v * t.darken)) as RGB;
  }
  return { ground: hex(ground), accent: hex(accent) };
}

/**
 * The mean colour of a book's cover, read from its preview picture on this
 * origin (`/og/books/{id}`, ADR-0209 §2): the bucket's signed covers send no
 * CORS headers, so their pixels would taint the canvas. A book a stranger may
 * not see redirects to the generic `/og.jpg`; that, or any failure, is "no
 * cover", and the card takes the look's own colours.
 */
export async function coverMean(bookID: string, open: boolean): Promise<RGB | null> {
  if (!open) return null;
  try {
    const res = await fetch(`/og/books/${encodeURIComponent(bookID)}`, { credentials: "omit" });
    if (!res.ok || res.redirected || new URL(res.url).pathname === "/og.jpg") return null;
    const bitmap = await createImageBitmap(await res.blob());
    const canvas = document.createElement("canvas");
    // A downscale's mean is the mean within the spec's tolerance.
    canvas.width = 32;
    canvas.height = Math.max(1, Math.round((32 * bitmap.height) / bitmap.width));
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return meanColour(ctx.getImageData(0, 0, canvas.width, canvas.height).data);
  } catch {
    return null;
  }
}

// Layout ---------------------------------------------------------------------

export interface CardQuote {
  text: string;
  book_title: string;
  author?: string;
  chapter_title?: string;
  lang?: string;
}

/** What measures text: the canvas's own context, or a fake in unit tests. */
export interface Measure {
  width(text: string, font: string): number;
}

export interface CardContent {
  quote: string;
  title: string;
  byline: string;
  direction: Dir;
  quoteDirection: Dir;
  look: CardLook;
  ground: string;
  accent: string;
  /** The quote's size, or null when it does not fit even at the floor. */
  size: number | null;
}

const font = (face: string, size: number, opts: { italic?: boolean; weight?: number } = {}) =>
  `${opts.italic ? "italic " : ""}${opts.weight ?? 400} ${size}px "${face}", "${SPEC.faces.fallback}"`;

export function quoteFont(c: CardContent, size: number) {
  return font(c.look.quoteFace, size, { italic: c.look.italicLatin && c.quoteDirection === "ltr" });
}
const titleFont = (c: CardContent) =>
  font(c.look.metaFace, SPEC.title.size, { weight: SPEC.title.weight });
const bylineFont = (c: CardContent) => font(c.look.metaFace, SPEC.byline.size);

/**
 * Breaks text into lines at spaces, never inside a word: a word wider than
 * the line stands alone on it. With maxLines, the last line kept ends in an
 * ellipsis (the title and byline only; a quote is never cut).
 */
export function wrap(
  text: string,
  fontSpec: string,
  measure: Measure,
  maxLines?: number,
): string[] {
  const max = SPEC.width - 2 * SPEC.padX;
  const lines: string[] = [];
  let line = "";
  for (const word of text.split(/ +/).filter(Boolean)) {
    const next = line ? `${line} ${word}` : word;
    if (line && measure.width(next, fontSpec) > max) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  if (maxLines && lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    let last = kept[maxLines - 1]!;
    while (last && measure.width(`${last}…`, fontSpec) > max)
      last = Array.from(last).slice(0, -1).join("");
    kept[maxLines - 1] = `${last.trimEnd()}…`;
    return kept;
  }
  return lines;
}

const lineHeight = (c: CardContent) =>
  c.quoteDirection === "rtl" ? SPEC.quote.lineRtl : SPEC.quote.lineLtr;

function metaHeight(c: CardContent, measure: Measure): number {
  const title = wrap(c.title, titleFont(c), measure, SPEC.title.maxLines).length;
  let h = title * SPEC.title.size * SPEC.title.line;
  if (c.byline) {
    const by = wrap(c.byline, bylineFont(c), measure, SPEC.byline.maxLines).length;
    h += SPEC.byline.gap + by * SPEC.byline.size * SPEC.byline.line;
  }
  return h;
}

const BODY = SPEC.height - SPEC.padTop - SPEC.padBottom - SPEC.footer.height - SPEC.footer.ruleGap;
const RULE = SPEC.rule.before + SPEC.rule.height + SPEC.rule.after;

/** Everything the card draws, worked out once, with the quote fitted. */
export function cardContent(
  q: CardQuote,
  look: CardLook,
  mean: RGB | null,
  measure: Measure,
): CardContent {
  const direction = cardDirection(q.lang);
  const quoteDirection = directionOf(q.text, direction);
  const iso = (s: string) => `\u2068${s}\u2069`;
  const tint = tintFor(look, mean);
  const c: CardContent = {
    quote: isolateRuns(q.text.trim(), quoteDirection),
    title: iso(q.book_title),
    byline: [q.author, q.chapter_title]
      .filter((s): s is string => !!s)
      .map(iso)
      .join(" · "),
    direction,
    quoteDirection,
    look,
    ground: tint.ground,
    accent: tint.accent,
    size: null,
  };
  const room = BODY - RULE - metaHeight(c, measure);
  for (let s: number = SPEC.quote.max; s >= SPEC.quote.floor; s -= SPEC.quote.step) {
    const lines = wrap(c.quote, quoteFont(c, s), measure).length;
    if (lines * s * lineHeight(c) <= room) return { ...c, size: s };
  }
  return c;
}

/** The fonts a card uses, loaded before it is measured or drawn. */
export async function loadCardFonts(c: CardContent) {
  const all = `${c.quote}${c.title}${c.byline}${SPEC.footer.name}${SPEC.footer.address}`;
  await Promise.all(
    [
      quoteFont(c, SPEC.quote.max),
      titleFont(c),
      bylineFont(c),
      font(SPEC.faces.serif, SPEC.footer.nameSize, { weight: 700 }),
    ].map((f) => document.fonts.load(f, all).catch(() => [])),
  );
}

// Drawing --------------------------------------------------------------------

/** The SageLeaf mark: web/public/favicon.svg's bars in its 32-unit box. */
function drawMark(ctx: CanvasRenderingContext2D, x: number, y: number, size: number) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 32, size / 32);
  ctx.translate(16, 16);
  ctx.rotate((-20 * Math.PI) / 180);
  ctx.translate(-16, -16);
  const g = ctx.createLinearGradient(4, 27, 29, 6);
  g.addColorStop(0, "#4A7A2A");
  g.addColorStop(0.4, "#6B9D3E");
  g.addColorStop(1, "#7FC5E0");
  ctx.fillStyle = g;
  const bars: [number, number, number][] = [
    [3.5, 11.5, 9],
    [9.3, 7.5, 17],
    [15.1, 4.5, 23],
    [20.9, 7.5, 17],
    [26.7, 11.5, 9],
  ];
  for (const [bx, by, h] of bars) {
    ctx.beginPath();
    ctx.roundRect(bx, by, 3.4, h, 1.7);
    ctx.fill();
  }
  ctx.restore();
}

/** Writes lines of one style from `top`, each centred in its line box. */
function drawLines(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  fontSpec: string,
  size: number,
  line: number,
  dir: Dir,
  colour: string,
  top: number,
): number {
  ctx.font = fontSpec;
  ctx.fillStyle = colour;
  ctx.direction = dir;
  ctx.textAlign = dir === "rtl" ? "right" : "left";
  ctx.textBaseline = "alphabetic";
  const x = dir === "rtl" ? SPEC.width - SPEC.padX : SPEC.padX;
  const m = ctx.measureText("Hg");
  const ascent = m.fontBoundingBoxAscent ?? size * 0.9;
  const descent = m.fontBoundingBoxDescent ?? size * 0.25;
  const box = size * line;
  let y = top;
  for (const l of lines) {
    ctx.fillText(l, x, y + (box - (ascent + descent)) / 2 + ascent);
    y += box;
  }
  return y;
}

/** Draws the card on a canvas of SPEC's size; the content must fit. */
export function drawCard(canvas: HTMLCanvasElement, c: CardContent) {
  canvas.width = SPEC.width;
  canvas.height = SPEC.height;
  const ctx = canvas.getContext("2d")!;
  const measure: Measure = {
    width: (t, f) => {
      ctx.font = f;
      return ctx.measureText(t).width;
    },
  };
  const size = c.size ?? SPEC.quote.floor;
  const look = c.look;
  ctx.fillStyle = c.ground;
  ctx.fillRect(0, 0, SPEC.width, SPEC.height);
  if (look.bar) {
    ctx.fillStyle = c.accent;
    ctx.fillRect(0, 0, SPEC.width, look.bar.height);
  }
  if (look.frame) {
    const { inset, width } = look.frame;
    ctx.strokeStyle = c.accent;
    ctx.lineWidth = width;
    ctx.strokeRect(
      inset + width / 2,
      inset + width / 2,
      SPEC.width - 2 * inset - width,
      SPEC.height - 2 * inset - width,
    );
  }

  const quote = wrap(c.quote, quoteFont(c, size), measure);
  const title = wrap(c.title, titleFont(c), measure, SPEC.title.maxLines);
  const byline = c.byline ? wrap(c.byline, bylineFont(c), measure, SPEC.byline.maxLines) : [];
  const block =
    quote.length * size * lineHeight(c) +
    RULE +
    title.length * SPEC.title.size * SPEC.title.line +
    (byline.length ? SPEC.byline.gap + byline.length * SPEC.byline.size * SPEC.byline.line : 0);
  let y = SPEC.padTop + (BODY - block) / 2;
  y = drawLines(ctx, quote, quoteFont(c, size), size, lineHeight(c), c.quoteDirection, look.ink, y);
  y += SPEC.rule.before;
  ctx.fillStyle = c.accent;
  const ruleX = c.direction === "rtl" ? SPEC.width - SPEC.padX - SPEC.rule.width : SPEC.padX;
  ctx.fillRect(ruleX, y, SPEC.rule.width, SPEC.rule.height);
  y += SPEC.rule.height + SPEC.rule.after;
  y = drawLines(
    ctx,
    title,
    titleFont(c),
    SPEC.title.size,
    SPEC.title.line,
    c.direction,
    c.accent,
    y,
  );
  if (byline.length) {
    drawLines(
      ctx,
      byline,
      bylineFont(c),
      SPEC.byline.size,
      SPEC.byline.line,
      c.direction,
      look.muted,
      y + SPEC.byline.gap,
    );
  }

  // The footer: a hairline halfway down the gap, then the mark and the name
  // at the card's start and the address at its end.
  const f = SPEC.footer;
  const bodyEnd = SPEC.padTop + BODY;
  ctx.fillStyle = look.line;
  ctx.fillRect(SPEC.padX, bodyEnd + f.ruleGap / 2, SPEC.width - 2 * SPEC.padX, f.rule);
  const top = SPEC.height - SPEC.padBottom - f.height;
  const mid = top + f.height / 2;
  const rtl = c.direction === "rtl";
  const start = rtl ? SPEC.width - SPEC.padX - f.mark : SPEC.padX;
  drawMark(ctx, start, mid - f.mark / 2, f.mark);
  ctx.textBaseline = "middle";
  ctx.direction = "ltr";
  ctx.font = font(SPEC.faces.serif, f.nameSize, { weight: 700 });
  ctx.fillStyle = look.brand;
  ctx.textAlign = rtl ? "right" : "left";
  ctx.fillText(f.name, rtl ? start - f.markGap : start + f.mark + f.markGap, mid);
  ctx.font = font(look.metaFace, f.addressSize);
  ctx.fillStyle = look.muted;
  ctx.textAlign = rtl ? "left" : "right";
  ctx.fillText(f.address, rtl ? SPEC.padX : SPEC.width - SPEC.padX, mid);
}

/** A canvas-backed measure for laying out before drawing. */
export function canvasMeasure(): Measure {
  const ctx = document.createElement("canvas").getContext("2d")!;
  return {
    width: (t, f) => {
      ctx.font = f;
      return ctx.measureText(t).width;
    },
  };
}
